/* eslint-disable no-extra-boolean-cast */
/* eslint-disable max-len */
import { useRef, useState } from "react";

import { RcFile } from "antd/es/upload";
import { message, Table, Upload, UploadProps } from "antd";
import { useTranslation } from "react-i18next";

import { AddFileFilled, CloseWindowOutlined, DeleteOutlined, DownloadDocumentIcon, Spinner } from "common/lib/components";
import { documentsApiReducer } from "domains/documents/store/documents.api.reducer";
import { allowedMimeTypes } from "domains/documents/shared/constants";
import { getIconForFile, truncateFileName } from "domains/documents/shared/utils";
import { apiService } from "common/shared";
import { useClientsSelector } from "domains/clients";
import { getDateFromISO } from "common/shared/utils/date.utils";
import { useScreen } from "common/shared-hooks";
import { DocumentPasswordForm } from "./DocumentPasswordForm";

import "./documentsUpload.scss";

const DocumentsUpload = ({ documentProps, _onFirstDocumentUploaded }:any) => {
  const [ isDeleting, setIsDeleting ] = useState(false)
  const { user } = useClientsSelector();
  const { t } = useTranslation([ "liquidity", "common" ]);
  const { mobile } = useScreen();
  const [ uploadDocument ] = documentsApiReducer.useUploadDocumentMutation();
  const [ deleteDocument ] = documentsApiReducer.useDeleteDocumentMutation({
    fixedCacheKey: "delete-document",
  });
  const uploadQueue = useRef<any[]>([]);
  const isUploading = useRef(false);
  const beforeUploadTriggered = useRef(false);
  const uploadProps: UploadProps = {
    accept: allowedMimeTypes.join(","),
    multiple: true,
    showUploadList: false,
    onDrop: (event: any) => {
      const invalid = Array.from(event.dataTransfer!.files).some((file: any) => !allowedMimeTypes.includes(file.type));
      if (invalid) {
        message.error(t("validation.onlyPDF", { ns: "common" }));
      }
    },
    beforeUpload: (file: RcFile, _fileList): boolean => {
      if (!beforeUploadTriggered.current) {
        beforeUploadTriggered.current = true;
        if (documentProps.beforeUpload) {
          documentProps.beforeUpload();
        }
    
        setTimeout(() => {
          beforeUploadTriggered.current = false;
        }, 100);
      }
      if (!allowedMimeTypes.includes(file.type)) {
        message.error(t("validation.onlyPDF", { ns: "common" }));
        return false;
      }

      if ((file.size ?? 0) > 500 * 1_000_000) {
        message.error(t("validation.fileSize", { ns: "common", value: 500 }));
        return false;
      }
      return true;
    },
    customRequest: async (options) => {
      const file = options.file as RcFile;
      
      const doc: any = {
        file_name: truncateFileName(file.name.replace(/[^- _a-zA-Z0-9!.'()]/g, "-")),
        document_type: documentProps.document_type,
        reference_type: documentProps.reference_type,
        file_size: file.size,
        id: file.uid,
        uid: file.uid,
        cancelToken: apiService.getCancelToken(),
        mimeType: file.type,
      };
      
      if (documentProps.asset_id) {
        doc.asset_id = documentProps.asset_id;
      }
      
      if (documentProps.document_name) {
        doc.document_name = documentProps.document_name;
      }
      
      if (documentProps.liquidity_request_id) {
        doc.liquidity_request_id = documentProps.liquidity_request_id;
      }

      uploadQueue.current.push({ file, doc });
      documentProps.setDocumentList((prevList: any) => [ ...prevList, { doc, file, uploadStatus: 'pending' } ]);
      processQueue();

    },
  };

  const processQueue = async () => {
    if (isUploading.current || uploadQueue.current.length === 0) {
      return;
    }
    
    isUploading.current = true;

    const next = uploadQueue.current.shift();
    if (!next) {
      return;
    }

    try {
      await uploadDocument({
        accountId: user?.account_id as string,
        document: next.doc as any,
        file: next.file,
      });
    } catch (error) {
      console.log({ error });
    }

    isUploading.current = false;
    
    try {
      await documentProps.onUpload();
    } catch (error) {
      console.log({ error });
    }
    
    processQueue();
  };

  const renderTable = () => {
    const columns: any = [
      {
        title: "Document",
        key: "assetName",
        width: "25%",
        render: () => (
          <Upload.Dragger { ...uploadProps }>
            <p>{t("Drag and drop files here")}</p>
          </Upload.Dragger>
        ),
        onCell: (_: any, rowIndex: any) => ({
          rowSpan: rowIndex === 0 ? documentProps.documentList?.length : 0,
        }),
        className: "ben-drug-n-drop-section",
      },
      {
        key: "file_name",
        width: "25%",
        title: t("File Name"),
        dataIndex: "file_name",
        render: (file_name: string, document: any) => (
          <div>
            {getIconForFile(document.doc.file_name)}
            <p>{document.doc.file_name}</p>
          </div>
        ),
        className: "ben-file-name-section",
      },
      {
        key: "date_provided",
        width: "20%",
        title: t("Date Provided"),
        dataIndex: "date_provided",
        render: (date_provided: string, doc: any) => {
          return <p>{doc.doc.percent ? `${ t("Uploading...") } ${ doc.doc.percent }%` : `${ getDateFromISO(doc.doc.date_provided) }`}</p>;
        },
        className: "ben-date-provided-section",
      },
      {
        key: "document_password",
        width: "30%",
        title: t("Document Password, If Applicable"),
        dataIndex: "document_password",
        render: (document_password: string, document: any) => {
          if (user) {
            return <DocumentPasswordForm document={ document.doc } accountId={ user.account_id } /> 
          }
        },
        className: "ben-document-password-section",
      },
      {
        key: "download_action",
        width: "7%",
        render: (document: any) => (
          user 
            ? document.uploadStatus && document.uploadStatus === 'uploaded' ? <DownloadDocumentIcon documentId={ document.doc.id } accountId={ user.account_id } iconClassName="ben-download-file-icon" /> : <></>
            : <></>
        ),
        className: "ben-action-section",
      },
      {
        key: "delete_action",
        width: "5%",
        render: (document: any) => {
          if (document.uploadStatus) {
            if (document.uploadStatus === 'pending') {
              return <Spinner spinning={ true }></Spinner>
            }
            if (document.uploadStatus === 'uploaded') {
              return (
                <DeleteOutlined
                  onClick={ async () => {
                    setIsDeleting(true)
                      await deleteDocument({
                        accountId: user?.account_id ?? '',
                        documentId: document.doc.id,
                      });
                      documentProps.onDelete()
                    setIsDeleting(false)
                  } }
                />
              );
            }
          }
          if (document.doc.cancelToken) {
            return (
              <CloseWindowOutlined
                onClick={ () => {
                  apiService.cancel(document.doc.cancelToken)
                } }
              />
            )
          }
        },
        className: "ben-action-section",
      },
    ];

    return (
      <>
        <Table
          className="ben-asset-documents-table"
          locale={ {
            emptyText: (
              <Upload.Dragger { ...uploadProps } openFileDialogOnClick={ true }>
                <p>{t("Drag and drop files here")}</p>
              </Upload.Dragger>
            ),
          } }
          dataSource={ documentProps.documentList }
          rowKey={ (doc, index) =>  doc.doc.uid || doc.doc.id || `doc-${ index }` }
          columns={ columns }
          pagination={ false }
          scroll={ { y: 400 } }
        />
      </>
    );
  };

  const renderMobileView = () => {
    return (
      <div className="ben-asset-documents-list-mobile">
        <h4>Document</h4>
        <Upload { ...uploadProps } openFileDialogOnClick={ true }>
          <div>
            <AddFileFilled className="ben-icon" width={ 30 } height={ 30 } />
            <p>{t("Add File")}</p>
          </div>
        </Upload>

        {documentProps.documentList.map(({ doc: document }: any, index: number) => {
          return (
            <div key={ `file-info-${ document.uid || document.id || index }` }>
              <div className="ben-files-list-item">
                {getIconForFile(document.file_name)}
                <p>{document.file_name}</p>
                <p className="ben-pl-2">
                  {document.percent
                    ? `${ t("Uploading...") } ${ document.percent }%`
                    : `${ getDateFromISO(document.date_provided) }`}
                </p>

                {user && <DownloadDocumentIcon
                  documentId={ document.id }
                  accountId={ user.account_id }
                  spinnerClassName="ben-download-file-icon"
                  iconClassName="ben-download-file-icon"
                />}

                {document.cancelToken ? (
                  <CloseWindowOutlined
                    className="ben-delete-file-btn"
                    onClick={ () => apiService.cancel(document.cancelToken) }
                  />
                ) : (
                  <DeleteOutlined
                    className="ben-delete-file-btn"
                    onClick={ async () => {
                      setIsDeleting(true)
                        await deleteDocument({
                          accountId: user?.account_id ?? '',
                          documentId: document.id,
                        });
                      setIsDeleting(false)
                    } }
                  /> 
                )}
              </div>
              <div className="ben-files-list-item">
                {getIconForFile(document.file_name)}
                <p className="ben-w-45">{document.file_name}</p>
                <p className="ben-w-50">
                  { user && <DocumentPasswordForm key={ document.id } document={ document } accountId={ user.account_id } /> }
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (mobile) {
    return (
      <Spinner spinning={ isDeleting }>
        { renderMobileView() }
      </Spinner>
    )
  }

  return (
    <Spinner spinning={ isDeleting }>
      {renderTable() }
    </Spinner>
  )
  
};

export default DocumentsUpload;
